/**
 * ActiveFiltersBar — F-006 Phase 5 (IMPL-P5-005).
 *
 * Chip row showing every active filter (including `q`). Each chip has an `X`
 * to remove just that filter, plus a "Azzera tutti" link at the end.
 */
import { X } from 'lucide-react';
import { useArchivio } from '../hooks/useArchivio';
import { Button } from '@/components/ui/button';

const LABELS: Record<string, string> = {
  q: 'Ricerca',
  tipologia: 'Tipologia',
  stato: 'Stato',
  parte: 'Parte',
  dateFrom: 'Dal',
  dateTo: 'Al',
};

function formatValue(key: string, value: string): string {
  if (key === 'dateFrom' || key === 'dateTo') {
    return value.slice(0, 10);
  }
  return value;
}

export function ActiveFiltersBar() {
  const { searchTerm, setSearchTerm, filters, setFilter, clearFilters, hasActiveFilters } =
    useArchivio();

  if (!hasActiveFilters) return null;

  const chips: Array<{ key: string; value: string; onRemove: () => void }> = [];
  if (searchTerm) {
    chips.push({ key: 'q', value: searchTerm, onRemove: () => setSearchTerm('') });
  }
  for (const [k, v] of Object.entries(filters)) {
    if (!v) continue;
    chips.push({
      key: k,
      value: v,
      onRemove: () => setFilter(k as 'tipologia' | 'stato' | 'parte' | 'dateFrom' | 'dateTo', undefined),
    });
  }

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      role="region"
      aria-label="Filtri attivi"
    >
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 rounded-full border bg-muted px-3 py-1 text-xs"
        >
          <span className="font-medium text-muted-foreground">{LABELS[chip.key] ?? chip.key}:</span>
          <span>{formatValue(chip.key, chip.value)}</span>
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={`Rimuovi filtro ${LABELS[chip.key] ?? chip.key}`}
            className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-background"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={clearFilters}
        className="h-auto p-0 text-xs"
      >
        Azzera tutti
      </Button>
    </div>
  );
}
